/**
 * User Modal Component
 * Add/Edit users with role assignment
 */

import { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Shield, Mail, User as UserIcon, Eye, EyeOff, Check } from 'lucide-react';
import Modal from '../shared/Modal';
import { api } from '../../services/api';

interface Role {
  _id: string;
  name: string;
  slug: string;
  level: number;
}

interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  roleIds: string[];
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  companyId: string;
}

interface Company {
  _id: string;
  name: string;
}

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: User;
  currentUserRoles?: string[];
}

const UserModal = ({ isOpen, onClose, user, currentUserRoles = [] }: UserModalProps) => {
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);
  const isSuperAdmin =
    currentUserRoles.includes('super-admin') || currentUserRoles.includes('super_admin');

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    password: '',
    roleIds: [] as string[],
    status: 'active' as const,
    companyId: '',
  });

  // Fetch roles
  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const response = await api.get('/api/v1/roles');
      return response.data;
    },
    staleTime: 0, // Always fetch fresh data
    cacheTime: 0, // Don't cache
  });

  // Fetch companies (super admin only)
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.get('/api/v1/companies').then(res => res.data.data),
    enabled: isSuperAdmin,
  });

  const roles: Role[] = rolesData?.data || [];
  const companies: Company[] = companiesData || [];

  useEffect(() => {
    if (user) {
      // Ensure roleIds are strings (in case API returns populated objects)
      const roleIds = Array.isArray(user.roleIds)
        ? user.roleIds.map((role: any) => (typeof role === 'string' ? role : role._id))
        : [];

      setFormData({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone || '',
        password: '', // Don't show password on edit
        roleIds,
        status: user.status,
        companyId: user.companyId,
      });
    } else {
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        password: '',
        roleIds: [],
        status: 'active',
        companyId: companiesData?.[0]?._id || '',
      });
    }
  }, [user, isOpen, companiesData]);

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (user) {
        const { password, ...updateData } = data;
        return api.put(`/api/v1/users/${user._id}`, password ? data : updateData);
      } else {
        return api.post('/api/v1/users', data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(user ? 'User updated successfully' : 'User created successfully');
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to save user');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user && !formData.password) {
      toast.error('Password is required for new users');
      return;
    }
    saveMutation.mutate(formData);
  };

  const toggleRole = (roleId: string) => {
    setFormData(prev => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter(id => id !== roleId)
        : [...prev.roleIds, roleId],
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={user ? 'Edit User' : 'New User'} size="xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <UserIcon className="w-5 h-5 text-primary-600" />
            Basic Information
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                className="input"
                placeholder="John"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                className="input"
                placeholder="Doe"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  className="input pl-10"
                  placeholder="john.doe@company.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                className="input"
                placeholder="+44 20 1234 5678"
              />
            </div>
          </div>

          {/* Password (only for new users or when changing) */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Password {!user && <span className="text-red-500">*</span>}
              {user && (
                <span className="text-sm text-secondary-500 ml-2">
                  (leave blank to keep current)
                </span>
              )}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required={!user}
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
                className="input pr-10"
                placeholder="••••••••"
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {!user && <p className="text-xs text-secondary-500 mt-1">Minimum 8 characters</p>}
          </div>
        </div>

        {/* Company (Super Admin Only) */}
        {isSuperAdmin && companies.length > 0 && (
          <div className="border-t pt-4">
            <label className="block text-sm font-medium mb-2">
              Company <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.companyId}
              onChange={e => setFormData({ ...formData, companyId: e.target.value })}
              className="input"
              required
            >
              <option value="">Select Company</option>
              {companies.map(company => (
                <option key={company._id} value={company._id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Roles */}
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-primary-600" />
            Roles & Permissions
          </h3>

          <div className="grid grid-cols-2 gap-3">
            {roles.map(role => (
              <button
                key={role._id}
                type="button"
                onClick={() => toggleRole(role._id)}
                className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                  formData.roleIds.includes(role._id)
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-secondary-200 dark:border-secondary-700 hover:border-primary-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {role.name}
                      {formData.roleIds.includes(role._id) && (
                        <Check className="w-4 h-4 text-primary-600" />
                      )}
                    </div>
                    <div className="text-xs text-secondary-500">Level {role.level}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {formData.roleIds.length === 0 && (
            <p className="text-sm text-amber-600 mt-2">⚠️ Please select at least one role</p>
          )}
        </div>

        {/* Status */}
        <div className="border-t pt-4">
          <label className="block text-sm font-medium mb-2">Status</label>
          <div className="grid grid-cols-4 gap-2">
            {(['active', 'inactive', 'suspended', 'pending'] as const).map(status => (
              <button
                key={status}
                type="button"
                onClick={() => setFormData({ ...formData, status })}
                className={`p-3 rounded-lg border-2 transition-all duration-200 capitalize ${
                  formData.status === status
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-secondary-200 dark:border-secondary-700 hover:border-primary-300'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saveMutation.isPending || formData.roleIds.length === 0}
            className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending ? 'Saving...' : user ? 'Update User' : 'Create User'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default UserModal;
